// (B) 플레인 Pagination — antd Pagination 대체(순수 시각). antd DOM 미러링:
// ul.om-pagination > li.om-pagination-{prev|item[-active]|jump-prev|jump-next|next|options}.
// size changer 는 §7-D 확정대로 native <select> 자체 구현(OmSelect 배치5 종속 회피).
// 소비처 SCSS(.om-pagination*)가 시각 정의 → 여기선 구조+antd base(styles/omPagination.css)만.
import type { OmwPaginationProps } from '../api/OmwPagination'
import { cx } from './cx'
import '../styles/omwPagination.css'

const DEFAULT_PAGE_SIZE = 10
const JUMP_SPAN = 5

// antd 유사 페이저 계산: 총 7페이지 이하는 전부, 이상은 1·현재±2·마지막 + 점프 생략(...).
function getPagerItems(
  current: number,
  totalPages: number,
): (number | 'jump-prev' | 'jump-next')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const items: (number | 'jump-prev' | 'jump-next')[] = [1]
  const left = Math.max(2, current - 2)
  const right = Math.min(totalPages - 1, current + 2)
  if (left > 2) items.push('jump-prev')
  for (let page = left; page <= right; page += 1) items.push(page)
  if (right < totalPages - 1) items.push('jump-next')
  items.push(totalPages)
  return items
}

export function PlainPagination({
  current = 1,
  total = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  onChange,
  showSizeChanger,
  pageSizeOptions,
  onPageSizeChange,
  sizeChangerSuffixIcon,
  className,
  style,
}: OmwPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const goTo = (page: number) => {
    const next = Math.min(Math.max(1, page), totalPages)
    if (next !== current) onChange?.(next, pageSize)
  }

  const options = pageSizeOptions ?? [10, 20, 50, 100]

  return (
    <ul className={cx('om-pagination', className)} style={style}>
      <li
        className={cx('om-pagination-prev', current <= 1 && 'om-pagination-disabled')}
        onClick={() => goTo(current - 1)}
      >
        <button type="button" className="om-pagination-item-link" disabled={current <= 1}>
          ‹
        </button>
      </li>

      {getPagerItems(current, totalPages).map((item, idx) => {
        if (item === 'jump-prev' || item === 'jump-next') {
          const target = item === 'jump-prev' ? current - JUMP_SPAN : current + JUMP_SPAN
          return (
            <li
              key={`${item}-${idx}`}
              className={`om-pagination-${item}`}
              onClick={() => goTo(target)}
            >
              <span className="om-pagination-item-ellipsis">•••</span>
            </li>
          )
        }
        return (
          <li
            key={item}
            className={cx('om-pagination-item', item === current && 'om-pagination-item-active')}
            onClick={() => goTo(item)}
          >
            <a>{item}</a>
          </li>
        )
      })}

      <li
        className={cx('om-pagination-next', current >= totalPages && 'om-pagination-disabled')}
        onClick={() => goTo(current + 1)}
      >
        <button type="button" className="om-pagination-item-link" disabled={current >= totalPages}>
          ›
        </button>
      </li>

      {showSizeChanger && (
        <li className="om-pagination-options">
          <div className="om-pagination-size-changer">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
              aria-label="page size"
            >
              {options.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            {sizeChangerSuffixIcon != null && (
              <span className="om-pagination-size-changer-suffix">{sizeChangerSuffixIcon}</span>
            )}
          </div>
        </li>
      )}
    </ul>
  )
}
